import { type JSX, useId, useState } from 'react'

import { Popover, PopoverContent, PopoverTrigger } from './ui/popover.tsx'
import { useTheme } from './theme-provider.tsx'
import { useTimeZone } from './time-zone-provider.tsx'
import { Button } from '../design-system/button.tsx'
import { Icon } from '../design-system/icon.tsx'
import { Select } from '../design-system/select.tsx'
import { cn } from '../lib/cn.ts'
import type { Theme } from '../lib/theme.ts'
import { timeZoneLabel, timeZoneOptions } from '../lib/time-zone.ts'
import { useSession } from '../session.tsx'

const OPTIONS = timeZoneOptions()

const THEME_NAMES: Record<Theme, string> = { light: 'Light', dark: 'Dark' }

const THEMES: readonly Theme[] = ['light', 'dark']

export interface AccountMenuProps {
  /** The prefix of every hook inside: `-menu`, `-user`, `-sign-out` and so on. */
  'data-testid': string
  /** Closes the mobile sheet before a sign-out runs. */
  onSelect: () => void
}

/**
 * Up to two letters for a name: one per word of an address before its `@`.
 *
 * A word that starts with a digit gives none, so the uid fallback `uid-1`
 * reads `U` rather than `U1`.
 *
 * @param name - An address, or the uid when the token carries no email.
 * @returns The letters, or `?` when the name holds none.
 */
function initialsOf(name: string): string {
  const letters = (name.split('@')[0] ?? '')
    .split(/[._\-\s]+/)
    .filter(word => /^\p{L}/u.test(word))
    .map(word => (Array.from(word)[0] ?? '').toUpperCase())
    .slice(0, 2)
    .join('')

  return letters || '?'
}

/**
 * AccountMenu — who is signed in, how figures read, and the way out.
 *
 * One rail row stands for all of it, so the foot stays one item however many
 * apps the page list gains. The row still prints the zone and the theme,
 * because the zone changes what every report says, and a reader must see it
 * without opening anything.
 *
 * The theme is a Light and Dark pair over the provider's one `toggle`. A press
 * on the half already on screen does nothing, which keeps ADR-0005: `system`
 * is reachable only by never having chosen.
 *
 * The popover is a light surface, so its controls are the page's own `Select`
 * and `Button` rather than rail rows.
 *
 * @param props - Menu props.
 * @param props.data-testid - Prefix for every test hook inside.
 * @param props.onSelect - Closes the mobile sheet before a sign-out.
 * @returns The rendered menu.
 */
export function AccountMenu({
  'data-testid': testId,
  onSelect
}: AccountMenuProps): JSX.Element {
  const { userName, onSignOut } = useSession()
  const { timeZone, setTimeZone } = useTimeZone()
  const { theme, toggle } = useTheme()
  const themeLabelId = useId()
  const options = OPTIONS.some(option => option.value === timeZone)
    ? OPTIONS
    : [{ value: timeZone, label: timeZone }, ...OPTIONS]
  const zone = timeZoneLabel(timeZone)
  const summary = `${zone} · ${THEME_NAMES[theme]}`
  const [open, setOpen] = useState(false)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`${testId}-menu`}
          // The row's text is three nodes, and a name built from them joins
          // them with no separator. The label keeps the visible words, so a
          // voice command that reads them still finds the control.
          aria-label={`${userName}, ${summary}, account and display settings`}
          className={cn(
            'text-sidebar-foreground/80 flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm',
            'hover:bg-sidebar-accent/40 focus-visible:ring-sidebar-ring cursor-pointer focus-visible:ring-2 focus-visible:outline-none',
            'data-[state=open]:bg-sidebar-accent/60'
          )}
        >
          <span
            data-testid={`${testId}-initials`}
            aria-hidden="true"
            className="bg-sidebar-accent text-sidebar-accent-foreground flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold"
          >
            {initialsOf(userName)}
          </span>
          <span className="flex min-w-0 flex-1 flex-col">
            <span
              data-testid={`${testId}-user`}
              className="text-sidebar-foreground truncate"
            >
              {userName}
            </span>
            <span
              data-testid={`${testId}-summary`}
              title={summary}
              className="text-sidebar-foreground/60 truncate text-xs"
            >
              {summary}
            </span>
          </span>
          <Icon name="chevrons-up-down" className="size-4 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        data-testid={`${testId}-menu-content`}
        aria-label="Account and display settings"
        side="top"
        align="start"
        className="w-72 p-0"
      >
        <div className="px-4 py-3">
          <p className="text-muted-foreground text-xs">Signed in as</p>
          <p
            data-testid={`${testId}-menu-user`}
            className="text-sm font-medium break-all"
          >
            {userName}
          </p>
        </div>
        <div className="grid gap-3 border-t px-4 py-3">
          <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
            Display
          </p>
          <Select
            data-testid={`${testId}-time-zone`}
            label="Time zone"
            options={options}
            value={timeZone}
            onChange={event => setTimeZone(event.target.value)}
          />
          <div
            role="group"
            aria-labelledby={themeLabelId}
            className="grid gap-2"
          >
            <span
              id={themeLabelId}
              className="text-sm leading-none font-medium"
            >
              Theme
            </span>
            <div className="grid grid-cols-2 gap-2">
              {THEMES.map(choice => (
                <Button
                  key={choice}
                  data-testid={`${testId}-theme-${choice}`}
                  variant={theme === choice ? 'primary' : 'outline'}
                  aria-pressed={theme === choice}
                  onClick={() => {
                    if (theme !== choice) {
                      toggle()
                    }
                  }}
                >
                  <Icon
                    name={choice === 'light' ? 'sun' : 'moon'}
                    className="size-4"
                  />
                  {THEME_NAMES[choice]}
                </Button>
              ))}
            </div>
          </div>
        </div>
        <div className="border-t p-1">
          <Button
            data-testid={`${testId}-sign-out`}
            variant="quiet"
            className="w-full justify-start"
            onClick={() => {
              setOpen(false)
              onSelect()
              onSignOut()
            }}
          >
            <Icon name="log-out" className="size-4" />
            Sign out
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
